import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';

import {
  AdminGoalLifecycleView,
  GoalStatus,
  GoalsService,
} from '../../../../services/goals.service';
import { TherapistService } from '../../../../services/therapist.service';
import {
  TherapistFilter,
  TherapistGridRequest,
} from '../../../../models/therapistGridRequest';

/**
 * APH-4995 T5+T6+T8 — read-only goal-lifecycle view + support override actions (pause/resume/
 * abandon/change-expert), embedded into the existing user-profile search page. A separate
 * component (not folded into search-user.component.ts's generic dynamic-panel mechanism) because
 * this data comes from a DEDICATED endpoint (T2), not searchUserProfile's response — mirrors the
 * lyfis-trace-detail precedent of a separate drill-down view rather than a generic panel.
 */
@Component({
  selector: 'app-goals-panel',
  templateUrl: './goals-panel.component.html',
})
export class GoalsPanelComponent implements OnChanges, OnDestroy {
  @Input() userId!: string;

  private readonly destroy$ = new Subject<void>();

  readonly GoalStatus = GoalStatus;

  loading = false;
  loadError = false;
  view: AdminGoalLifecycleView | null = null;

  isAbandonModalOpen = false;
  abandonReason = '';
  actionLoading = false;

  isChangeExpertModalOpen = false;
  expertOptions: { id: string; name: string }[] = [];
  expertOptionsLoading = false;
  selectedNewExpertId: string | null = null;

  isMarkDayCompleteModalOpen = false;
  markDayCompleteUserObjectiveId: string | null = null;
  markDayCompleteDate: Date | null = null;
  markDayCompleteReason = '';

  constructor(
    private goalsService: GoalsService,
    private therapistService: TherapistService,
    private modal: NzModalService,
    private toastrService: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId'] && this.userId) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.goalsService
      .getGoalLifecycleForUser(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          this.view = value;
          this.loading = false;
          this.loadError = false;
        },
        error: (err) => {
          this.loading = false;
          this.loadError = true;
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(
            errorBody?.description ?? 'Failed to load goal details',
          );
        },
      });
  }

  canPause(): boolean {
    return this.view?.goal?.status === GoalStatus.ACTIVE;
  }

  canResume(): boolean {
    return this.view?.goal?.status === GoalStatus.PAUSED;
  }

  canAbandonOrChangeExpert(): boolean {
    const status = this.view?.goal?.status;
    return status === GoalStatus.ACTIVE || status === GoalStatus.PAUSED;
  }

  pauseGoal(): void {
    this.modal.confirm({
      nzTitle: 'Pause this goal?',
      nzContent: `This will pause the user's current goal and freeze its tracked targets.`,
      nzOnOk: () => this.runAction(this.goalsService.pauseGoal(this.userId)),
    });
  }

  resumeGoal(): void {
    this.modal.confirm({
      nzTitle: 'Resume this goal?',
      nzContent: `This will re-activate the user's paused goal and re-apply its targets.`,
      nzOnOk: () => this.runAction(this.goalsService.resumeGoal(this.userId)),
    });
  }

  openAbandonModal(): void {
    this.abandonReason = '';
    this.isAbandonModalOpen = true;
  }

  confirmAbandon(): void {
    this.runAction(
      this.goalsService.abandonGoal(this.userId, this.abandonReason || undefined),
    ).then(() => {
      this.isAbandonModalOpen = false;
    });
  }

  /**
   * Returns a Promise so `nzOnOk` (modal.confirm) keeps its confirm dialog open
   * with a loading state until the request settles, instead of closing synchronously.
   * Resolves to whether the action succeeded; no-ops (resolves false) if an action
   * is already in flight, guarding against overlapping duplicate requests.
   */
  private runAction(
    obs: ReturnType<GoalsService['pauseGoal']>,
  ): Promise<boolean> {
    if (this.actionLoading) {
      return Promise.resolve(false);
    }
    this.actionLoading = true;
    return new Promise<boolean>((resolve) => {
      obs.pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.actionLoading = false;
          if (res.success) {
            this.toastrService.showSuccess('Goal updated successfully');
            this.load();
          } else {
            this.toastrService.showAnyoErrorToast(res.error ?? 'Action failed');
          }
          resolve(res.success);
        },
        error: (err) => {
          this.actionLoading = false;
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(
            errorBody?.description ?? 'Action failed',
          );
          resolve(false);
        },
      });
    });
  }

  openChangeExpertModal(): void {
    this.selectedNewExpertId = null;
    this.isChangeExpertModalOpen = true;
    this.expertOptionsLoading = true;
    const req: TherapistGridRequest = {
      page: 1,
      pageSize: 200,
      export: false,
      filters: {
        [TherapistFilter.GOALS_EXPERT]: ['true'],
        [TherapistFilter.TEST_THERAPIST]: ['false'],
        [TherapistFilter.THERAPIST_RATING]: undefined,
        [TherapistFilter.THERAPIST_STATUS]: undefined,
      },
    };
    this.therapistService
      .listTherapist(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.expertOptions = res.data.map((t) => ({
            id: t.id ?? '',
            name: `${t.firstName} ${t.lastName}`.trim(),
          }));
          this.expertOptionsLoading = false;
        },
        error: () => {
          this.expertOptionsLoading = false;
          this.toastrService.showAnyoErrorToast('Failed to load experts');
        },
      });
  }

  /** nzDisabledDate predicate — today or in the past only, mirroring the backend's own
   * validateDateNotInFuture check (no point letting the picker offer a date the API will reject). */
  isFutureDate = (date: Date): boolean => {
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    return date.getTime() > endOfToday.getTime();
  };

  openMarkDayCompleteModal(userObjectiveId: string): void {
    this.markDayCompleteUserObjectiveId = userObjectiveId;
    this.markDayCompleteDate = null;
    this.markDayCompleteReason = '';
    this.isMarkDayCompleteModalOpen = true;
  }

  confirmMarkDayComplete(): void {
    if (!this.markDayCompleteUserObjectiveId || !this.markDayCompleteDate) {
      return;
    }
    this.actionLoading = true;
    this.goalsService
      .markObjectiveDayComplete(
        this.markDayCompleteUserObjectiveId,
        this.markDayCompleteDate.getTime(),
        this.markDayCompleteReason,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.actionLoading = false;
          this.isMarkDayCompleteModalOpen = false;
          if (res.success) {
            this.toastrService.showSuccess('Day marked complete');
            this.load();
          } else {
            this.toastrService.showAnyoErrorToast(res.error ?? 'Action failed');
          }
        },
        error: (err) => {
          this.actionLoading = false;
          this.isMarkDayCompleteModalOpen = false;
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(
            errorBody?.description ?? 'Action failed',
          );
        },
      });
  }

  confirmChangeExpert(): void {
    if (!this.selectedNewExpertId) return;
    this.actionLoading = true;
    this.goalsService
      .changeExpert(this.userId, this.selectedNewExpertId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.actionLoading = false;
          this.isChangeExpertModalOpen = false;
          if (res.success) {
            this.toastrService.showSuccess('Expert updated successfully');
            this.load();
          } else {
            this.toastrService.showAnyoErrorToast(res.error ?? 'Action failed');
          }
        },
        error: (err) => {
          this.actionLoading = false;
          this.isChangeExpertModalOpen = false;
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(
            errorBody?.description ?? 'Action failed',
          );
        },
      });
  }
}
